import {Box, Button, CircularProgress, Container, FormControlLabel, styled, Switch, TextField, Typography} from '@mui/material';
import Logo from 'assets/robot-logo.svg';
import {get_cookie, get_local_storage, remove_local_storage, save_cookie, save_local_storage} from 'common';
import BG from 'components/layout/BG';
import {Footer} from 'components/layout/Footer';
import Layout from 'components/layout/Layout';
import {request_ping} from 'connector/account';
import CryptoJS from 'crypto-js';
import {useAlert} from 'hooks/generalHooks';
import {useAuth} from 'hooks/userHooks';
import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import package_json from '../../package.json';

const SECRET_KEY = 'SJWIJNDIW293892';

const ContentWrapper = styled(Box)({
	position: 'relative',
	textAlign: 'center',
	color: '#fff',
	padding: '2rem',
	backgroundColor: 'rgba(255, 255, 255, 0.1)',
	borderRadius: '16px',
	backdropFilter: 'blur(5px)',
	boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
});

export default function LoginPage() {
	const version = package_json.version;

	const {login, is_logged_in} = useAuth();
	const {showAlert} = useAlert();
	const [is_server_live, set_is_server_live] = useState(false);
	const [server_version, set_server_version] = useState('');

	const navigate = useNavigate();

	const prev_username = get_local_storage('username') ?? process.env.REACT_APP_TEST_ID ?? '';
	let prev_password = process.env.REACT_APP_TEST_PW ?? '';

	const encrypted_password = get_local_storage('password');
	if (encrypted_password) {
		try {
			const decrypted = CryptoJS.AES.decrypt(encrypted_password, SECRET_KEY);
			prev_password = decrypted.toString(CryptoJS.enc.Utf8) || prev_password;
		} catch (error) {
			console.error('Password decryption failed:', error);
		}
	}

	const [formData, setFormData] = useState({username: prev_username, password: prev_password});
	const [errors, setErrors] = useState({username: '', password: ''});
	const [loading, setLoading] = useState(false);
	const [is_save, set_is_save] = useState(get_cookie('save_credential') === 'true');

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const {name, value} = e.target;
		setFormData({...formData, [name]: value});

		if (name === 'username' && value) {
			const isValidUsername = /^[a-zA-Z]+$/.test(value);
			setErrors({...errors, username: isValidUsername ? '' : 'Username should contain only alphabetic characters'});
		}
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		let isValid = true;
		const newErrors = {...errors};

		if (!formData.username) {
			newErrors.username = 'Username is required';
			isValid = false;
		} else if (!/^[a-zA-Z]+$/.test(formData.username)) {
			newErrors.username = 'Username should contain only alphabetic characters';
			isValid = false;
		}

		if (!formData.password) {
			newErrors.password = 'Password is required';
			isValid = false;
		}

		setErrors(newErrors);

		if (isValid) {
			setLoading(true);

			const params = new URLSearchParams();
			params.append('username', formData.username);
			params.append('password', formData.password);

			const res: boolean = await login(params);
			if (res) {
				if (is_save) {
					save_local_storage('username', formData.username);
					const encrypted_password = CryptoJS.AES.encrypt(formData.password, SECRET_KEY).toString();
					save_local_storage('password', encrypted_password);
				}

				showAlert('Login Success!', 'success');
				navigate('/', {replace: true});
			} else showAlert('Login Failed', 'error');

			setLoading(false);
		}
	};

	useEffect(() => {
		if (!is_save) {
			remove_local_storage('username');
			remove_local_storage('password');
		}

		save_cookie('save_credential', is_save ? 'true' : 'false');
	}, [is_save]);

	useEffect(() => {
		if (is_logged_in) navigate('/', {replace: true});
	}, [is_logged_in]);

	useEffect(() => {
		const checkServerStatus = async () => {
			try {
				const resp = await request_ping();
				set_is_server_live(resp.code === 200);
				set_server_version(`${resp.data.version}-${resp.data.env}`);
			} catch (error) {
				console.error('Error checking server status:', error);
			}
		};

		checkServerStatus();
	}, []);

	return is_logged_in ? null : (
		<BG type="lightning">
			<Layout footer={Footer}>
				<Box width="100%" flexGrow={1} display="flex" alignItems="center" justifyContent="center">
					<Container maxWidth="xs">
						<ContentWrapper>
							<Box component={'img'} src={Logo} alt="Logo" width={100} />

							<Typography variant="h5" sx={{fontFamily: 'Playwrite RO'}}>
								Coin Copy Machine
							</Typography>
							<Typography variant="caption">{`${version}·Srv ${server_version}`}</Typography>

							<Box id="form-box" component="form" onSubmit={handleSubmit}>
								<TextField
									margin="normal"
									required
									fullWidth
									id="username"
									label="Username"
									name="username"
									autoComplete="username"
									autoFocus
									value={formData.username}
									onChange={handleChange}
									error={!!errors.username}
									helperText={errors.username}
								/>

								<TextField
									margin="normal"
									required
									fullWidth
									name="password"
									label="Password"
									type="password"
									id="password"
									autoComplete="current-password"
									value={formData.password}
									onChange={handleChange}
									error={!!errors.password}
									helperText={errors.password}
								/>

								<FormControlLabel
									sx={{width: '100%', margin: 0}}
									control={<Switch checked={is_save} onChange={e => set_is_save(e.target.checked)} name="save" color="primary" size="small" />}
									label={<Typography variant="caption">Save Username and Password</Typography>}
								/>

								<Button type="submit" fullWidth variant="contained" sx={{mt: 3, mb: 2}} disabled={loading || !is_server_live}>
									{loading ? <CircularProgress size={24} /> : 'Sign In'}
								</Button>
							</Box>
						</ContentWrapper>
					</Container>
				</Box>
			</Layout>
		</BG>
	);
}
